// src/components/admin/SystemConfigEditor.tsx — Task A: admin settings editor
//
// First useQuery in Admin.tsx. Reads system_config via the new
// /api/admin/settings endpoint, edits values inline, sends a POST, then
// invalidates the ['system_config'] query key. Optimistic update with
// rollback on error.

import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  Alert,
  AlertTitle,
  Stack,
  CircularProgress,
  Chip,
} from "@mui/material";
import { Save, Refresh, Settings as SettingsIcon } from "@mui/icons-material";
import { getSystemConfig, updateSystemConfig } from "~/lib/api/api-client";

interface ConfigRow {
  key: string;
  value: string;
  description: string | null;
}

interface SystemConfigEditorProps {
  adminPassword: string;
}

const EDITABLE_HINTS: Record<string, string> = {
  pts_val_min: "نسبة النقاط الدنيا (مثال: 0.0096 = 0.96٪)",
  pts_val_max: "نسبة النقاط القصوى (مثال: 0.0105 = 1.05٪)",
  pts_val_curve_steepness: "حدة منحنى الانخفاض (>1 = أسرع)",
};

export function SystemConfigEditor({ adminPassword }: SystemConfigEditorProps) {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savedKey, setSavedKey] = useState<string | null>(null);

  // Read the current system_config. staleTime is 0 so the GET fires again
  // when we invalidate() after a successful save.
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["system_config"],
    queryFn: async () => {
      const result = await getSystemConfig(adminPassword);
      if (!result.success) throw new Error(result.error || "Failed to load");
      return result.config || [];
    },
    staleTime: 0,
    retry: 1,
  });

  // Mirror server data into local draft state on every load.
  useEffect(() => {
    if (!data) return;
    const next: Record<string, string> = {};
    for (const row of data) next[row.key] = row.value;
    setDrafts((prev) => ({ ...next, ...prev }));
  }, [data]);

  // Save mutation. Optimistic: write the drafts to the cache, then
  // confirm with the server. On error, restore the previous snapshot.
  const saveMutation = useMutation({
    mutationFn: async (updates: Array<{ key: string; value: string }>) => {
      const result = await updateSystemConfig(updates, adminPassword);
      if (!result.success) throw new Error(result.error || "Failed to save");
      return result.config || [];
    },
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: ["system_config"] });
      const previous = queryClient.getQueryData<ConfigRow[]>(["system_config"]);
      queryClient.setQueryData<ConfigRow[]>(["system_config"], (old) => {
        if (!old) return old;
        const map = new Map(updates.map((u) => [u.key, u.value]));
        return old.map((row) =>
          map.has(row.key) ? { ...row, value: map.get(row.key)! } : row,
        );
      });
      return { previous };
    },
    onError: (_err, _updates, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["system_config"], context.previous);
      }
    },
    onSuccess: (fresh) => {
      // Sync the authoritative server response into the cache.
      queryClient.setQueryData(["system_config"], fresh);
      setSavedKey(new Date().toISOString());
    },
    onSettled: () => {
      // Always refetch so any concurrent server-side changes are picked up.
      queryClient.invalidateQueries({ queryKey: ["system_config"] });
    },
  });

  // Auto-clear the "saved" chip after 2.5s.
  useEffect(() => {
    if (!savedKey) return;
    const t = setTimeout(() => setSavedKey(null), 2500);
    return () => clearTimeout(t);
  }, [savedKey]);

  if (isLoading) {
    return (
      <Card sx={{ p: 3, borderRadius: 3, mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card sx={{ p: 3, borderRadius: 3, mb: 4 }}>
        <Alert severity="error">
          <AlertTitle>فشل تحميل الإعدادات</AlertTitle>
          {(error as Error)?.message || "حدث خطأ غير متوقع"}
          <Button
            size="small"
            startIcon={<Refresh />}
            onClick={() => refetch()}
            sx={{ mt: 1 }}
          >
            إعادة المحاولة
          </Button>
        </Alert>
      </Card>
    );
  }

  const rows = (data || []).filter((r) => r.key in EDITABLE_HINTS);

  const handleChange = (key: string, value: string) => {
    setDrafts((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const updates: Array<{ key: string; value: string }> = [];
    for (const row of rows) {
      const draft = drafts[row.key] ?? row.value;
      if (draft !== row.value) {
        updates.push({ key: row.key, value: draft });
      }
    }
    if (updates.length === 0) return;
    saveMutation.mutate(updates);
  };

  const dirtyCount = rows.reduce(
    (n, row) => n + ((drafts[row.key] ?? row.value) !== row.value ? 1 : 0),
    0,
  );

  return (
    <Card sx={{ p: 3, borderRadius: 3, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SettingsIcon color="primary" />
          <Typography variant="h5">إعدادات النظام</Typography>
          {savedKey && (
            <Chip
              size="small"
              color="success"
              label="✓ تم الحفظ"
              sx={{ fontWeight: 600 }}
            />
          )}
          {isFetching && !isLoading && (
            <CircularProgress size={16} sx={{ ml: 1 }} />
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => refetch()}
            disabled={isFetching}
          >
            تحديث
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saveMutation.isPending || dirtyCount === 0}
          >
            {saveMutation.isPending
              ? "جاري الحفظ..."
              : `حفظ (${dirtyCount})`}
          </Button>
        </Box>
      </Box>

      {saveMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>فشل الحفظ</AlertTitle>
          {(saveMutation.error as Error)?.message}
        </Alert>
      )}

      <Stack spacing={2}>
        {rows.map((row) => (
          <Box key={row.key}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 0.5, fontWeight: 500 }}
            >
              {row.key}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              {EDITABLE_HINTS[row.key] ||
                row.description ||
                "قيمة قابلة للتعديل"}
            </Typography>
            <TextField
              value={drafts[row.key] ?? row.value}
              onChange={(e) => handleChange(row.key, e.target.value)}
              size="small"
              fullWidth
              inputProps={{ dir: "ltr", style: { textAlign: "left" } }}
            />
          </Box>
        ))}
      </Stack>
    </Card>
  );
}
